import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/** Reads a single query param and re-renders when it changes. */
export const useTabParam = (key: string) => {
  const [searchParams] = useSearchParams();
  return searchParams.get(key);
};

export const useTabs = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const get = (key: string) => searchParams.get(key);

  const set = useCallback(
    (key: string, value: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(key, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const remove = useCallback(
    (key: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(key);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /** Sets several params at once; `undefined` removes the param. */
  const patch = useCallback(
    (values: Record<string, string | undefined>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          Object.entries(values).forEach(([key, value]) => {
            if (value === undefined) {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { get, set, remove, patch };
};
